"use client"

import type React from "react"
import { useSelector, useDispatch } from "react-redux"
import { Link, useNavigate } from "react-router-dom"
import type { RootState } from "../store/store"
import { removeFromWishlist, clearWishlist } from "../store/slices/wishlistSlice"
import { addToCart } from "../store/slices/cartSlice"
import { Heart, ShoppingCart, Trash2 } from "lucide-react"
import toast from "react-hot-toast"
import { useEffect } from 'react'
import { userService } from '../services/userService'
import SEOHead from "../components/common/SEOHead"
import LazyImage from "../components/common/LazyImage"

// Helper to slugify strings for URLs
function slugify(str: string) {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

const Wishlist: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { items } = useSelector((state: RootState) => state.wishlist)
  const { user } = useSelector((state: RootState) => state.auth)

  useEffect(() => {
    if (user) {
      userService.updateWishlist(user.uid, items)
    }
  }, [items, user])

  const handleRemoveFromWishlist = (id: string) => {
    dispatch(removeFromWishlist(id))
    toast.success("Removed from wishlist")
  }

  const handleAddToCart = (item: any) => {
    if (!user) {
      toast.error("Please login to add items to cart")
      return
    }

    dispatch(
      addToCart({
        id: item.id,
        name: item.name,
        price: item.price,
        image: item.image || item.images?.[0],
        category: item.category || item.categories?.[0],
      }),
    )
    dispatch(removeFromWishlist(item.id))
    toast.success("Added to cart!")
  }

  const handleClearWishlist = () => {
    dispatch(clearWishlist())
    toast.success("Wishlist cleared")
  }

  // Generate product URL consistent with ProductCard
  const generateProductUrl = (item: any) => {
    const primaryCategory = item.categories?.[0] || item.category || 'furniture';
    const categorySlug = slugify(primaryCategory);
    const nameSlug = slugify(item.name) || item.id;
    return `/${categorySlug}/${nameSlug}`;
  }

  if (items.length === 0) {
    return (
      <>
        <SEOHead
          title="Your Wishlist | Anmol Furniture Bazaar"
          description="View and manage your wishlist at Anmol Furniture Bazaar. Save your favorite furniture items for later."
          keywords="wishlist, favorite furniture, save products, premium furniture"
        />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
          <div className="text-center">
            <Heart className="w-16 sm:w-24 h-16 sm:h-24 text-gray-300 mx-auto mb-6" />
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">Your wishlist is empty</h2>
            <p className="text-gray-600 mb-8">Save items you love to your wishlist.</p>
            <Link
              to="/"
              className="bg-primary-600 text-white px-6 sm:px-8 py-3 rounded-lg hover:bg-primary-700 transition-colors font-medium"
            >
              Continue Shopping
            </Link>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <SEOHead
        title="Your Wishlist | Anmol Furniture Bazaar"
        description="View and manage your wishlist at Anmol Furniture Bazaar. Save your favorite furniture items for later."
        keywords="wishlist, favorite furniture, save products, premium furniture"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "Wishlist",
          "itemListElement": items.map((item, idx) => ({
            "@type": "ListItem",
            "position": idx + 1,
            "url": `https://anmolfurniture.com/products/${item.id}`,
            "name": item.name,
            "image": item.images?.[0] || item.image,
            "description": item.categories?.[0] || item.category,
          })),
        }}
      />
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 sm:mb-8 space-y-4 sm:space-y-0">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">My Wishlist</h1>
              <p className="text-gray-600">{items.length} items saved</p>
            </div>

            {items.length > 0 && (
              <button onClick={handleClearWishlist} className="text-red-600 hover:text-red-700 font-medium text-sm sm:text-base">
                Clear Wishlist
              </button>
            )}
          </div>

          {/* Wishlist Items - Responsive grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {items.map((item) => (
              <div key={item.id} className="bg-white rounded-xl shadow-md overflow-hidden group">
                <div className="relative">
                  <Link to={generateProductUrl(item)}>
                    {/* Using LazyImage component like ProductCard */}
                    <div className="w-full aspect-square rounded-lg overflow-hidden">
                      <LazyImage
                        src={item.images?.[0] || item.image || ''}
                        alt={item.name || 'Product'}
                        className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                        width={400}
                        height={400}
                      />
                    </div>
                  </Link>

                  {/* Remove Button - Better positioning */}
                  <button
                    onClick={() => handleRemoveFromWishlist(item.id)}
                    className="absolute top-2 sm:top-3 right-2 sm:right-3 p-1.5 sm:p-2 bg-white rounded-full shadow-lg hover:bg-red-50 transition-colors"
                  >
                    <Trash2 className="w-3 h-3 sm:w-4 sm:h-4 text-red-600" />
                  </button>
                </div>

                <div className="p-3 sm:p-4">
                  <Link
                    to={generateProductUrl(item)}
                    className="text-sm sm:text-lg font-semibold text-gray-900 hover:text-primary-600 transition-colors line-clamp-2 block mb-2"
                  >
                    {item.name}
                  </Link>

                  <p className="text-xs sm:text-sm text-gray-500 capitalize mb-3 sm:mb-4">
                    {item.categories?.[0] || item.category}
                  </p>

                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                    <span className="text-lg sm:text-xl font-bold text-primary-600">₹{item.price.toLocaleString()}</span>

                    <button
                      onClick={() => handleAddToCart(item)}
                      className="flex items-center justify-center space-x-2 bg-primary-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors text-sm"
                    >
                      <ShoppingCart className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span>Add to Cart</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  )
}

export default Wishlist